import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Page, Spinner } from "../../components";
import { useAdminRunningWorkflows } from "../../api/hooks/admin";
import AdminController from "../../controllers/AdminController";
import { handleAdminLoginAsUser } from "../../utils/adminLoginAsUser";
import Button from "../../components/Button";
import Select from "react-select";
import LogoutButton from "../../components/LogoutButton";
import "./styles.css";

type FilterType = "all" | "stuck" | "failed" | "running";
type SortKey =
  | "workflow_type"
  | "status"
  | "reason"
  | "user_name"
  | "agency_name"
  | "client_name"
  | "display_name"
  | "generation_time";
type SortDir = "asc" | "desc";

const STATUS_BADGE: Record<string, string> = {
  stuck: "#f5a623",
  failed: "#f44336",
  running: "#1b3bcc",
};

const HOURS_STUCK_THRESHOLD = 3;

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  proposal: "Proposal",
  contract_review: "Contract Review",
  coverage_gap_analysis: "Gap Analysis",
  certificate_of_insurance: "COI",
  acord_forms: "ACORD Forms",
  loss_run: "Loss Run",
  sov: "SOV",
};

function getTaskTypeLabel(workflowType: string, instanceIds?: string[]): string {
  if (workflowType === "report_run") {
    return (instanceIds?.length ?? 0) > 1 ? "Comparison" : "Analysis";
  }
  return WORKFLOW_TYPE_LABELS[workflowType] ?? workflowType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Use a portal for dropdown menus to avoid clipping in scrollable/overflow-hidden containers
const selectMenuPortalTarget =
  typeof document !== "undefined" ? document.body : undefined;

const DATE_SHORTCUTS = [
  { label: "All time", days: null },
  { label: "Last 30 days", days: 30 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 3 days", days: 3 },
];

const REFRESH_INTERVALS = [
  { value: null, label: "Off" },
  { value: 10, label: "Every 10 seconds" },
  { value: 30, label: "Every 30 seconds" },
  { value: 60, label: "Every 1 minute" },
  { value: 300, label: "Every 5 minutes" },
];

function durationHours(generationTime?: string): number {
  if (!generationTime) return 0;
  const created = new Date(generationTime).getTime();
  const now = Date.now();
  return (now - created) / (1000 * 60 * 60);
}

function getStatusBadge(status: string, hours: number) {
  if (status === "failed") return { label: "Failed", color: STATUS_BADGE.failed };
  if (status === "generated") return { label: "Completed", color: "#4caf50" };
  if (status === "analyzing" && hours > HOURS_STUCK_THRESHOLD) {
    return { label: "Stuck", color: STATUS_BADGE.stuck };
  }
  if (status === "analyzing") return { label: "Running", color: STATUS_BADGE.running };
  return { label: status, color: "#888" };
}

const WorkflowsPage: React.FC = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<string | null>(() => {
    const start = new Date();
    start.setDate(start.getDate() - 3);
    return start.toISOString();
  });
  const [endDate, setEndDate] = useState<string | null>(() => new Date().toISOString());
  const { data, isLoading, refetch, isFetching } = useAdminRunningWorkflows(startDate, endDate);
  const [filter, setFilter] = useState<FilterType>("all");
  const [hideNotAPolicy, setHideNotAPolicy] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("generation_time");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [openaiDown, setOpenaiDown] = useState(false);
  const [anthropicDown, setAnthropicDown] = useState(false);
  const [downLoading, setDownLoading] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    AdminController.getDownIndicators().then((indicators) => {
      setOpenaiDown(indicators.openai);
      setAnthropicDown(indicators.anthropic);
    });
  }, []);

  const handleToggleDown = async (resource: "openai" | "anthropic") => {
    const prev = resource === "openai" ? openaiDown : anthropicDown;
    const next = !prev;
    const setter = resource === "openai" ? setOpenaiDown : setAnthropicDown;
    setter(next);
    setDownLoading(resource);
    try {
      const ok = await AdminController.setDownIndicator(resource, next);
      if (!ok) {
        setter(prev);
        alert(`Failed to update ${resource} down indicator`);
      }
    } catch {
      setter(prev);
      alert(`Failed to update ${resource} down indicator`);
    } finally {
      setDownLoading(null);
    }
  };

  const workflows = data || [];

  const filteredAndSorted = useMemo(() => {
    const filtered = workflows.filter((w) => {
      if (hideNotAPolicy && w.reason === "not_a_policy") return false;
      const hours = durationHours(w.generation_time);
      const isStuck = w.status === "analyzing" && hours > HOURS_STUCK_THRESHOLD;
      if (filter === "all") return true;
      if (filter === "stuck") return isStuck;
      if (filter === "failed") return w.status === "failed";
      if (filter === "running") return w.status === "analyzing" && !isStuck;
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const valA = (a as any)[sortKey];
      const valB = (b as any)[sortKey];

      if (sortKey === "generation_time") {
        const timeA = valA ? new Date(valA).getTime() : 0;
        const timeB = valB ? new Date(valB).getTime() : 0;
        return (timeA - timeB) * dir;
      }

      if (valA === valB) return 0;
      if (valA === undefined || valA === null) return -1 * dir;
      if (valB === undefined || valB === null) return 1 * dir;

      return String(valA).localeCompare(String(valB)) * dir;
    });

    return sorted;
  }, [workflows, filter, hideNotAPolicy, sortKey, sortDir]);

  const handleMarkFailed = async (workflowType: string, workflowId: string, userId?: string) => {
    const confirm = window.confirm("Mark this workflow as failed?");
    if (!confirm) return;
    try {
      setActionLoading(workflowId);
      const ok = await AdminController.markWorkflowFailed(workflowType, workflowId);
      if (!ok) {
        alert("Failed to mark as failed");
      } else {
        await refetch();
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryWorkflow = async (workflowType: string, workflowId: string, userId?: string) => {
    const confirm = window.confirm("Retry this workflow?");
    if (!confirm) return;
    try {
      setActionLoading(workflowId);
      const ok = await AdminController.retryWorkflow(workflowType, workflowId, userId);
      if (!ok) {
        alert("Failed to retry workflow");
      } else {
        await refetch();
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleLoginAsUser = async (userId?: string) => {
    if (!userId) {
      alert("Missing user_id for login");
      return;
    }
    setActionLoading(userId);
    try {
      const success = await handleAdminLoginAsUser(userId);
      if (!success) {
        alert("Failed to login as user");
      }
    } finally {
      setActionLoading(null);
    }
  };

  const applyShortcut = (days: number | null) => {
    if (days === null) {
      setStartDate(null);
      setEndDate(null);
      return;
    }
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setStartDate(start.toISOString());
    setEndDate(end.toISOString());
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "generation_time" ? "desc" : "asc");
    }
  };

  // Auto-refresh functionality
  useEffect(() => {
    // Clear existing intervals
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    if (refreshInterval === null) {
      setCountdown(null);
      return;
    }

    // Set initial countdown
    setCountdown(refreshInterval);

    // Countdown timer
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          return refreshInterval;
        }
        return prev - 1;
      });
    }, 1000);

    // Auto-refresh timer
    intervalRef.current = setInterval(() => {
      refetch();
      setCountdown(refreshInterval);
    }, refreshInterval * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [refreshInterval, refetch]);

  // Reset countdown when manual refresh happens
  const handleManualRefresh = () => {
    refetch();
    if (refreshInterval !== null) {
      setCountdown(refreshInterval);
    }
  };

  const formatCountdown = (seconds: number | null): string => {
    if (seconds === null) return "";
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
  };

  return (
    <Page>
      {/* Header Section */}
      <div className="workflows-header" style={{ 
        width: "100%", 
        maxWidth: "80vw", 
        marginBottom: 24, 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        padding: "0 16px",
        boxSizing: "border-box" as const,
        flexWrap: "wrap" as const,
        gap: 8,
        margin: "0 auto 24px",
      }}>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: "clamp(32px, 6vw, 49px)",
            fontWeight: 600,
            color: "#1a1a1a",
          }}>
            Workflows
          </h1>
          <p style={{ 
            margin: "8px 0 0 0", 
            color: "#555", 
            fontSize: "clamp(14px, 2vw, 16px)",
            lineHeight: 1.5,
          }}>
            Stuck = analyzing for &gt; {HOURS_STUCK_THRESHOLD} hours (client-side).
          </p>
        </div>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 20,
          padding: "8px 16px",
          background: "#f8f9ff",
          borderRadius: 8,
          border: "1px solid #e0e4ef",
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>Down Indicators</span>
          {(["openai", "anthropic"] as const).map((resource) => {
            const isDown = resource === "openai" ? openaiDown : anthropicDown;
            const isLoading = downLoading === resource;
            const label = resource === "openai" ? "OpenAI" : "Anthropic";
            return (
              <label
                key={resource}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 500, color: "#444" }}>{label}</span>
                <div
                  onClick={() => !isLoading && handleToggleDown(resource)}
                  style={{
                    position: "relative",
                    width: 40,
                    height: 22,
                    borderRadius: 11,
                    background: isDown ? "#f44336" : "#4caf50",
                    transition: "background 0.2s ease",
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 2,
                      left: isDown ? 20 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "#fff",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      transition: "left 0.2s ease",
                    }}
                  />
                </div>
                <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: isDown ? "#f44336" : "#4caf50",
                  minWidth: 36,
                  textAlign: "center",
                }}>
                  {isDown ? "DOWN" : "UP"}
                </span>
              </label>
            );
          })}
        </div>

        <div style={{ 
          flexShrink: 0, 
          display: "flex", 
          alignItems: "center", 
          gap: 12,
          flexWrap: "wrap" as const,
        }}>
          <button
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "none",
              background: "#6b7280",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              transition: "background-color 0.2s ease",
            }}
            onClick={() => navigate("/")}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#4b5563";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#6b7280";
            }}
          >
            Back to Home
          </button>
          <LogoutButton />
        </div>
      </div>

      <div className="workflows-controls" style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <label>
          Filter:
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterType)}
            style={{ marginLeft: 8, padding: "6px 8px" }}
          >
            <option value="all">All</option>
            <option value="stuck">Stuck (&gt; 3h)</option>
            <option value="failed">Failed</option>
            <option value="running">Running (&le; 3h)</option>
          </select>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={hideNotAPolicy}
            onChange={(e) => setHideNotAPolicy(e.target.checked)}
          />
          Hide not_a_policy
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 14, color: "#444" }}>Date range:</span>
          {DATE_SHORTCUTS.map((s) => (
            <button
              key={s.label}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid #d0d4dc",
                background: "#fff",
                cursor: "pointer",
              }}
              onClick={() => applyShortcut(s.days)}
            >
              {s.label}
            </button>
          ))}
          <input
            type="date"
            value={startDate ? startDate.slice(0, 10) : ""}
            onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value).toISOString() : null)}
            style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #d0d4dc" }}
          />
          <input
            type="date"
            value={endDate ? endDate.slice(0, 10) : ""}
            onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value).toISOString() : null)}
            style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #d0d4dc" }}
          />
          <button
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #1b3bcc",
              background: "#1b3bcc",
              color: "#fff",
              cursor: "pointer",
            }}
            onClick={handleManualRefresh}
          >
            Apply
          </button>
          {isFetching && <span style={{ fontSize: 12, color: "#1b3bcc" }}>Refreshing…</span>}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, color: "#444" }}>Auto refresh:</span>
            <div style={{ minWidth: 160 }}>
              <Select
                value={REFRESH_INTERVALS.find((opt) => opt.value === refreshInterval) || REFRESH_INTERVALS[0]}
                onChange={(option) => setRefreshInterval(option?.value ?? null)}
                options={REFRESH_INTERVALS}
                isSearchable={false}
                isClearable={false}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 32,
                    height: 32,
                    fontSize: 14,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    height: 32,
                    padding: "0 6px",
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: 32,
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
            {refreshInterval !== null && countdown !== null && (
              <span style={{ fontSize: 14, color: "#1b3bcc", fontWeight: 500, minWidth: 50 }}>
                {formatCountdown(countdown)}
              </span>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
          <Spinner />
        </div>
      ) : (
        <>
          <div className="workflows-scroll-hint">← Swipe left/right to see all columns →</div>
          <div style={{ width: "100%", overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1100, fontSize: 13 }}>
              <thead>
                <tr>
                  <th onClick={() => toggleSort("workflow_type")} style={{ padding: "10px 12px", backgroundColor: "#3949ab", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" as const }}>
                    Type{sortKey === "workflow_type" ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : ""}
                  </th>
                  <th onClick={() => toggleSort("status")} style={{ padding: "10px 12px", backgroundColor: "#3949ab", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" as const }}>
                    Status{sortKey === "status" ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : ""}
                  </th>
                  <th onClick={() => toggleSort("reason")} style={{ padding: "10px 12px", backgroundColor: "#3949ab", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" as const }}>
                    Reason{sortKey === "reason" ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : ""}
                  </th>
                  <th onClick={() => toggleSort("user_name")} style={{ padding: "10px 12px", backgroundColor: "#3949ab", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" as const }}>
                    User{sortKey === "user_name" ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : ""}
                  </th>
                  <th onClick={() => toggleSort("agency_name")} style={{ padding: "10px 12px", backgroundColor: "#3949ab", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" as const }}>
                    Agency{sortKey === "agency_name" ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : ""}
                  </th>
                  <th onClick={() => toggleSort("client_name")} style={{ padding: "10px 12px", backgroundColor: "#3949ab", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" as const }}>
                    Client{sortKey === "client_name" ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : ""}
                  </th>
                  <th onClick={() => toggleSort("display_name")} style={{ padding: "10px 12px", backgroundColor: "#3949ab", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" as const }}>
                    Display Name{sortKey === "display_name" ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : ""}
                  </th>
                  <th onClick={() => toggleSort("generation_time")} style={{ padding: "10px 12px", backgroundColor: "#3949ab", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" as const }}>
                    Created{sortKey === "generation_time" ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : ""}
                  </th>
                  <th style={{ padding: "10px 12px", backgroundColor: "#3949ab", color: "#fff", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", userSelect: "none" as const }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSorted.map((w, idx) => {
                  const hours = durationHours(w.generation_time);
                  const badge = getStatusBadge(w.status, hours);
                  const isStuck = badge.label === "Stuck";
                  const noDocuments = w.workflow_type === "report_run" && !w.document_count;
                  const userText = w.user_name || w.user_id || "\u2014";
                  const agencyText = w.agency_name || w.agency_id || "\u2014";
                  const clientText = w.client_name || w.client_id || "\u2014";
                  const displayText = w.display_name || "\u2014";
                  const reasonText = w.reason || "\u2014";
                  return (
                    <tr key={`${w.workflow_type}-${w.workflow_id}`} style={{ backgroundColor: idx % 2 === 0 ? "#f8f9ff" : "#fff" }}>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "center" }}>{getTaskTypeLabel(w.workflow_type, w.instance_ids)}</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "center" }}>
                        <span
                          style={{
                            background: badge.color,
                            color: "#fff",
                            padding: "4px 8px",
                            borderRadius: 6,
                            fontWeight: 600,
                          }}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td title={w.reason_detail || reasonText} style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "center", whiteSpace: "nowrap", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{reasonText}</td>
                      <td title={userText} style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "center", whiteSpace: "nowrap", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{userText}</td>
                      <td title={agencyText} style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "center", whiteSpace: "nowrap", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{agencyText}</td>
                      <td title={clientText} style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "center", whiteSpace: "nowrap", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{clientText}</td>
                      <td title={displayText} style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "center", whiteSpace: "nowrap", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{displayText}</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "center" }}>{w.generation_time ? new Date(w.generation_time).toLocaleString() : "\u2014"}</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "left" }}>
                        <div className="action-buttons" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <Button
                            text="Mark Failed"
                            handleClick={() => handleMarkFailed(w.workflow_type, w.workflow_id, w.user_id)}
                            disabled={w.status === "failed" || actionLoading === w.workflow_id}
                          />
                          <span title={noDocuments ? "No documents to retry" : undefined}>
                            <Button
                              text="Retry"
                              handleClick={() => handleRetryWorkflow(w.workflow_type, w.workflow_id, w.user_id)}
                              disabled={actionLoading === w.workflow_id || noDocuments}
                            />
                          </span>
                          <Button
                            text="Login as User"
                            handleClick={() => handleLoginAsUser(w.user_id)}
                            disabled={!w.user_id || actionLoading === w.user_id}
                          />
                        </div>
                        {isStuck && (
                          <div style={{ color: "#f5a623", fontSize: 12, marginTop: 4 }}>
                            Stuck &gt; {HOURS_STUCK_THRESHOLD} hours
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!filteredAndSorted.length && (
                  <tr>
                    <td colSpan={9} style={{ padding: "8px 10px", borderBottom: "1px solid #e8e8e8", textAlign: "center" }}>
                      No workflows found for this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Page>
  );
};

export default WorkflowsPage;

